import type {
  ControlCenterAssetKind,
  ControlCenterLicenseType,
  ControlCenterMunicipalityLevel,
  ControlCenterPersonKind,
} from '../../../types/controlCenter'
import type { TigerwebBoundaryLevel } from '../../../services/tigerwebBoundaryService'
import type { BoundaryOverlayToggles, BoundaryShowAllLevel } from './controlCenterMapTypes'
import { normalizeLatLng } from '../../../utils/geoCoordinates'

// Note: this module intentionally focuses on "presentation helpers" (labels/colors/formatters).
// Domain logic like operator chains stays in `operations.ts`.

export const JOB_MARKER_COLOR = '#d97706'

const JOB_STATUS_COLOR: Record<string, string> = {
  created: '#64748b',
  pending: '#d97706',
  approved: '#2563eb',
  paid: '#059669',
}

export function jobStatusColor(status: string): string {
  const key = String(status || '').toLowerCase()
  return JOB_STATUS_COLOR[key] || JOB_MARKER_COLOR
}

export const PERSON_KIND_COLOR: Record<ControlCenterPersonKind, string> = {
  employee: '#0d9488',
  external: '#c026d3',
}

export function personKindLabel(kind: ControlCenterPersonKind): string {
  return kind === 'employee' ? 'Employee' : 'External'
}

export function formatClockedSince(startedAt: string | null): string {
  if (!startedAt) return '—'
  const start = Date.parse(startedAt)
  if (!Number.isFinite(start)) return startedAt
  const mins = Math.max(0, Math.round((Date.now() - start) / 60_000))
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  const rem = mins % 60
  if (hours < 24) return rem ? `${hours}h ${rem}m` : `${hours}h`
  const days = Math.floor(hours / 24)
  const remH = hours % 24
  return remH ? `${days}d ${remH}h` : `${days}d`
}

export function personHasMapPin(person: { clockedIn: boolean; lat: number | null; lng: number | null }): boolean {
  // Keep behavior identical: map pins only exist for clocked-in people with finite lat/lng.
  return Boolean(person.clockedIn && normalizeLatLng(person.lat as number, person.lng as number))
}

/** Nudge people off shared job pins so both icons stay visible. */
export function personMarkerPosition(lat: number, lng: number, besideJob: boolean): [number, number] {
  if (!besideJob) return [lat, lng]
  return [lat + 0.00014, lng + 0.00008]
}

export const ASSET_KIND_COLOR: Record<ControlCenterAssetKind, string> = {
  vehicle: '#2563eb',
  assetTracker: '#7c3aed',
}

export const ASSET_KIND_ORDER: ControlCenterAssetKind[] = ['vehicle', 'assetTracker']

export const MUNICIPALITY_LEVEL_COLOR: Record<ControlCenterMunicipalityLevel, string> = {
  state: '#2563eb',
  county: '#059669',
  city: '#d97706',
  federal: '#7c3aed',
}

export const MUNICIPALITY_LEVEL_ORDER: ControlCenterMunicipalityLevel[] = ['city', 'county', 'state', 'federal']

export const LICENSE_TYPE_COLOR: Record<ControlCenterLicenseType, string> = {
  sos_registration: '#0284c7',
  business: '#2563eb',
  contractor: '#d97706',
  electrical: '#ca8a04',
  other: '#64748b',
}

export const LICENSE_TYPE_ORDER: ControlCenterLicenseType[] = ['sos_registration', 'business', 'contractor', 'electrical', 'other']

export function assetKindLabel(kind: ControlCenterAssetKind): string {
  return kind === 'assetTracker' ? 'Asset tracker' : 'Vehicle / device'
}

export function statusTone(
  status: string,
): 'neutral' | 'error' | 'warning' | 'success' {
  const s = status.trim().toLowerCase()
  if (
    s === 'active' ||
    s === 'online' ||
    s === 'complete' ||
    s === 'moving' ||
    s === 'idle' ||
    s === 'approved' ||
    s === 'paid'
  ) {
    return s === 'idle' ? 'warning' : 'success'
  }
  if (
    s === 'planned' ||
    s === 'stopped' ||
    s === 'not active' ||
    s === 'pending' ||
    s === 'created'
  ) {
    return 'warning'
  }
  if (s.includes('offline') || s === 'degraded') return 'error'
  return 'neutral'
}

export function formatGpsMs(ms: number | null): string | null {
  if (ms == null || !Number.isFinite(ms)) return null
  try {
    return new Date(ms).toLocaleString()
  } catch {
    return null
  }
}

export function formatRoundedMiles(value: unknown): string | null {
  if (value == null || value === '') return null
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  return String(Math.round(n))
}

export function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    const area = document.createElement('textarea')
    area.value = text
    area.setAttribute('readonly', '')
    area.style.position = 'fixed'
    area.style.left = '-9999px'
    document.body.appendChild(area)
    area.select()
    try {
      return document.execCommand('copy')
    } finally {
      document.body.removeChild(area)
    }
  }
}

export function municipalityLicenseIsExpired(muni: {
  expirationDate: string
  licenseNumber?: string
  qualifier?: string | null
}): boolean {
  const number = String(muni.licenseNumber || '').trim().toLowerCase()
  if (number === 'reciprocity') return false
  if ((muni.qualifier || '').toLowerCase().includes('reciprocity')) return false
  if (!muni.expirationDate) return false
  const d = new Date(`${muni.expirationDate}T12:00:00`)
  if (Number.isNaN(d.getTime())) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return d.getTime() < today.getTime()
}

export function municipalityLicenseIsExpiringSoon(
  muni: { expirationDate: string; licenseNumber?: string; qualifier?: string | null },
  withinDays = 90,
): boolean {
  if (municipalityLicenseIsExpired(muni)) return false
  if (!muni.expirationDate) return false
  const d = new Date(`${muni.expirationDate}T12:00:00`)
  if (Number.isNaN(d.getTime())) return false
  const now = new Date()
  const diff = d.getTime() - now.getTime()
  return diff >= 0 && diff <= withinDays * 24 * 60 * 60 * 1000
}

export const BOUNDARY_OVERLAY_LEVELS: TigerwebBoundaryLevel[] = ['state', 'county', 'city']

export const EMPTY_BOUNDARY_OVERLAYS: BoundaryOverlayToggles = {
  state: false,
  county: false,
  city: false,
}

export const BOUNDARY_SHOW_ALL_OPTIONS: Array<{ value: BoundaryShowAllLevel; label: string }> = [
  { value: 'none', label: 'None' },
  { value: 'state', label: 'States' },
  { value: 'county', label: 'Counties' },
  { value: 'city', label: 'Cities' },
]

export function boundaryOverlaysFromShowLevel(level: BoundaryShowAllLevel): BoundaryOverlayToggles {
  return {
    state: level === 'state',
    county: level === 'county',
    city: level === 'city',
  }
}

export function showLevelFromBoundaryOverlays(overlays: BoundaryOverlayToggles): BoundaryShowAllLevel {
  if (overlays.state) return 'state'
  if (overlays.county) return 'county'
  if (overlays.city) return 'city'
  return 'none'
}

export function boundaryFitMaxZoom(level: ControlCenterMunicipalityLevel | string): number {
  // Prefer staying closer in; fitBounds still zooms out enough to fit large polys.
  if (level === 'state') return 8
  if (level === 'county') return 11
  return 13
}

